/**
 * Vehicles Service
 * Frontend service för att hämta fordon från API
 */

import api from '../api/client';
import type { VehiclePickupPlacement } from '@/types/vehicle';

export interface Vehicle {
  id: string;
  organization_id: string;
  organization_name?: string;  // Från JOIN med organizations
  registration_number: string;
  make?: string;
  model?: string;
  year?: number;
  status: 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE' | 'RETIRED';
  fuel_type?: string;
  current_driver_id?: number | string | null;
  /** Namn på användare kopplad via aktiv förartilldelning */
  assigned_user_name?: string | null;
  /** Fordonskategori (t.ex. Ambulans) */
  vehicle_category_name?: string | null;
  created_at: string;
  updated_at: string;
  device_imei?: string;
  device_id?: number;
  /** Aktuell firmware på enheten (FOTA) */
  device_firmware?: string | null;
  /** Aktuell konfiguration på enheten (FOTA) */
  device_configuration?: string | null;
  apn_sms_sent_at?: string | null;
  // Affärsfält
  is_poolcar?: boolean;
  cost_center?: string;
  reference_number?: string;
  external_key?: string;
  /** Antal sittplatser */
  seats?: number | null;
  /** Placeringsort (från Placering-fliken) */
  placement_city?: string | null;
  /** Kommaseparerad lista över utlämningsplatser fordonet är kopplat till */
  pickup_placement_names?: string | null;
  /** owned | leased | lease_ended */
  ownership_type?: string | null;
  transmission?: string | null;
  /** Mätarställning i meter (DB) */
  current_odometer?: number | null;
  flinkey_box_id?: string | null;
  alternative_fuel_id?: number | null;
  alternative_fuel_name?: string | null;
  business_responsibility_id?: number | null;
  business_responsibility_ansvar_text?: string | null;
  business_responsibility_ikpart_text?: string | null;
  /** Valbar fordonsstatus inom toppnod (leasing/avveckling). null = ingen status */
  usage_status_id?: number | null;
  usage_status_name?: string | null;
  // Däckinfo (monterat däckset)
  installed_tires?: string | null;
  installed_tire_type?: string | null;
  installed_tire_brand?: string | null;
  installed_tire_size?: string | null;
  installed_tire_tread_mm?: number | null;
  installed_tire_date?: string | null;
  // Realtid
  last_seen?: string;
  last_position?: {
    lat: number;
    lng: number;
    timestamp: string;
    speed?: number;
  };
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface VehiclesResponse {
  success: boolean;
  data: Vehicle[];
  pagination?: Pagination;
  error?: string;
}

export interface VehicleResponse {
  success: boolean;
  data: Vehicle;
  error?: string;
}

export interface VehicleQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  organizationId?: string;
  includeSubOrgs?: boolean;
  vehicleIds?: number[];
  connected?: string; // 'connected' | 'disconnected'
  presetFilter?: string; // 'poolcars' | 'mil' | 'tax-due' | 'inspection-due'
  usageStatusId?: string; // numeriskt id | 'none' (ingen status)
  sortBy?: string; // kolumnnyckel för server-side sortering
  sortDir?: 'asc' | 'desc';
  columnFilters?: Record<string, string>; // per-kolumn fritextfilter (server-side)
}

export interface PaginatedVehicles {
  vehicles: Vehicle[];
  pagination: Pagination;
}

class VehiclesService {
  /**
   * Hämta fordon med paginering
   */
  async getVehicles(params?: VehicleQueryParams): Promise<PaginatedVehicles> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.organizationId) queryParams.append('organizationId', params.organizationId);
      if (params?.includeSubOrgs !== undefined) queryParams.append('includeSubOrgs', params.includeSubOrgs.toString());
      if (params?.vehicleIds && params.vehicleIds.length > 0) {
        queryParams.append('vehicleIds', params.vehicleIds.join(','));
      }
      if (params?.connected) queryParams.append('connected', params.connected);
      if (params?.presetFilter) queryParams.append('presetFilter', params.presetFilter);
      if (params?.usageStatusId) queryParams.append('usageStatusId', params.usageStatusId);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortDir) queryParams.append('sortDir', params.sortDir);
      if (params?.columnFilters) {
        const active = Object.fromEntries(
          Object.entries(params.columnFilters).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
        );
        if (Object.keys(active).length > 0) {
          queryParams.append('columnFilters', JSON.stringify(active));
        }
      }

      const url = `/api/vehicles${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get<VehiclesResponse>(url);
      
      if (response.data.success) {
        return {
          vehicles: response.data.data,
          pagination: response.data.pagination || {
            page: 1,
            limit: 50,
            total: response.data.data.length,
            totalPages: 1
          }
        };
      }
      throw new Error(response.data.error || 'Failed to fetch vehicles');
    } catch (error: any) {
      console.error('Error fetching vehicles:', error);
      throw error;
    }
  }

  /**
   * Hämta alla fordon (utan paginering) - för bakåtkompatibilitet
   * Inkluderar automatiskt sub-organisationer
   */
  async getAllVehicles(): Promise<Vehicle[]> {
    const result = await this.getVehicles({ limit: 5000, includeSubOrgs: true });
    return result.vehicles;
  }

  /**
   * Sök fordon server-side (för autocomplete/select)
   * Returnerar max 50 träffar baserat på sökterm
   */
  async searchVehicles(search: string, organizationId?: string): Promise<Vehicle[]> {
    try {
      const params: VehicleQueryParams = {
        search,
        limit: 50,
        includeSubOrgs: true,
      };
      if (organizationId) params.organizationId = organizationId;
      const result = await this.getVehicles(params);
      return result.vehicles;
    } catch (error: any) {
      console.error('Error searching vehicles:', error);
      return [];
    }
  }

  /**
   * Hämta ett specifikt fordon
   */
  async getVehicleById(vehicleId: string): Promise<Vehicle> {
    try {
      const response = await api.get<VehicleResponse>(`/api/vehicles/${vehicleId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch vehicle');
    } catch (error: any) {
      console.error('Error fetching vehicle:', error);
      throw error;
    }
  }

  /** Utlämningsplatser där fordonet ingår (tom om ej i pool). */
  async getVehiclePickupPlacements(vehicleId: string): Promise<VehiclePickupPlacement[]> {
    try {
      const response = await api.get<{
        success: boolean;
        data: VehiclePickupPlacement[];
      }>(`/api/vehicles/${vehicleId}/pickup-placements`);
      if (response.data.success) {
        return response.data.data ?? [];
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching pickup placements:', error);
      return [];
    }
  }

  /**
   * Hämta bränsle/EV-status för flera fordon
   */
  async getVehicleFuelStatus(vehicleIds: string[]): Promise<Record<string, { fuelLevel: number | null; evBatteryCharge: number | null; evRemainingDistance: number | null; distanceUntilService: number | null }>> {
    try {
      const response = await api.post<{ success: boolean; data: Record<string, { fuelLevel: number | null; evBatteryCharge: number | null; evRemainingDistance: number | null; distanceUntilService: number | null }> }>(
        '/api/vehicles/fuel-status',
        { vehicleIds }
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('Failed to fetch fuel status');
    } catch (error: any) {
      console.error('Error fetching fuel status:', error);
      return {};
    }
  }
}

export const vehiclesService = new VehiclesService();
export default vehiclesService;

